// src/components/profile/ChangeEmailForm.js
import { useState } from 'react';

const ChangeEmailForm = ({ currentEmail, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    newEmail: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.newEmail) {
      newErrors.newEmail = 'New email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.newEmail)) {
      newErrors.newEmail = 'Please enter a valid email address';
    } else if (formData.newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      newErrors.newEmail = 'New email must be different from current email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-400 dark:border-yellow-800 rounded-lg p-4 mb-4">
      <div className="flex items-start mb-4">
        <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div>
          <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
            Important: Email Change Process
          </p>
          <ul className="text-xs text-yellow-700 dark:text-yellow-400 space-y-1">
            <li>• A verification email will be sent to your new email address</li>
            <li>• You must verify the new email before it becomes active</li>
            <li>• Your current email will be notified of this change</li>
            <li>• You won't be able to log in with the old email after verification</li>
          </ul>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="currentEmail" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
            Current Email
          </label>
          <input
            id="currentEmail"
            type="email"
            value={currentEmail}
            disabled
            className="w-full px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 rounded-lg bg-shayek-light-50 dark:bg-shayek-dark-50 text-shayek-light-600 dark:text-shayek-dark-600 cursor-not-allowed"
          />
        </div>

        <div>
          <label htmlFor="newEmail" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
            New Email Address *
          </label>
          <input
            id="newEmail"
            name="newEmail"
            type="email"
            value={formData.newEmail}
            onChange={handleChange}
            required
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 ${
              errors.newEmail ? 'border-red-500' : 'border-shayek-light-300 dark:border-shayek-dark-200'
            }`}
            placeholder="new@email.com"
          />
          {errors.newEmail && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.newEmail}</p>
          )}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
            Confirm Password *
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 ${
                errors.password ? 'border-red-500' : 'border-shayek-light-300 dark:border-shayek-dark-200'
              }`}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-shayek-light-600 dark:text-shayek-dark-600"
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l13.42 13.42" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 text-shayek-light-700 dark:text-shayek-dark-700 rounded-lg hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Sending...' : 'Request Email Change'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangeEmailForm;

